// Dates du calendrier. Un evenement horodate vit dans le fuseau du navigateur ;
// un evenement sur la journee est une date sans fuseau, portee en UTC (minuit
// du premier jour a minuit du lendemain du dernier), pour qu'il tombe le meme
// jour chez tout le monde.

const DAY_MS = 24 * 60 * 60 * 1000

export function startOfDay(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate())
}

export function addDays(date: Date, days: number): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate() + days)
}

export function addMonths(date: Date, months: number): Date {
  return new Date(date.getFullYear(), date.getMonth() + months, 1)
}

/** Lundi de la semaine de `date`. */
export function startOfWeek(date: Date): Date {
  const mondayOffset = (date.getDay() + 6) % 7
  return addDays(date, -mondayOffset)
}

export function weekDays(anchor: Date): Date[] {
  const monday = startOfWeek(anchor)
  return Array.from({ length: 7 }, (_, index) => addDays(monday, index))
}

/** Six semaines completes, du lundi qui precede (ou est) le 1er du mois. */
export function monthGrid(anchor: Date): Date[] {
  const first = startOfWeek(new Date(anchor.getFullYear(), anchor.getMonth(), 1))
  return Array.from({ length: 42 }, (_, index) => addDays(first, index))
}

/** Intervalle de jours `AAAA-MM-JJ`, bornes incluses. */
export interface SyncWindow {
  from: string
  to: string
}

/**
 * Periodes a mettre a jour pour un calendrier lie : celle affichee d'abord,
 * puis la precedente et la suivante.
 */
export function syncWindows(view: 'month' | 'week', anchor: Date): SyncWindow[] {
  const period = (offset: number): SyncWindow => {
    if (view === 'month') {
      const first = new Date(anchor.getFullYear(), anchor.getMonth() + offset, 1)
      return { from: dayKey(first), to: dayKey(new Date(first.getFullYear(), first.getMonth() + 1, 0)) }
    }

    const monday = addDays(startOfWeek(anchor), 7 * offset)
    return { from: dayKey(monday), to: dayKey(addDays(monday, 6)) }
  }

  return [period(0), period(-1), period(1)]
}

function pad(value: number): string {
  return String(value).padStart(2, '0')
}

/** `AAAA-MM-JJ` dans le fuseau du navigateur : cle d'un jour, et valeur d'un `<input type="date">`. */
export function dayKey(date: Date): string {
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`
}

/** `HH:MM` dans le fuseau du navigateur : valeur d'un `<input type="time">`. */
export function timeKey(date: Date): string {
  return `${pad(date.getHours())}:${pad(date.getMinutes())}`
}

export function fromDayKey(key: string): Date {
  const [year = 1970, month = 1, day = 1] = key.split('-').map(Number)
  return new Date(year, month - 1, day)
}

/** Instant ISO d'une date et d'une heure saisies dans le fuseau du navigateur. */
export function localToIso(day: string, time: string): string {
  const [hours = 0, minutes = 0] = time.split(':').map(Number)
  const date = fromDayKey(day)
  date.setHours(hours, minutes, 0, 0)
  return date.toISOString()
}

/** Bornes UTC d'un evenement sur la journee, du premier au dernier jour inclus. */
export function allDayToIso(firstDay: string, lastDay: string): { startsAt: string; endsAt: string } {
  const end = new Date(`${lastDay}T00:00:00.000Z`)
  return {
    startsAt: `${firstDay}T00:00:00.000Z`,
    endsAt: new Date(end.getTime() + DAY_MS).toISOString(),
  }
}

export interface Spanning {
  startsAt: string
  endsAt: string
  isAllDay: boolean
}

/** Premier et dernier jour (inclus) couverts par un evenement, en cles `AAAA-MM-JJ`. */
export function eventDays(event: Spanning): { first: string; last: string } {
  if (event.isAllDay) {
    const lastDay = new Date(new Date(event.endsAt).getTime() - DAY_MS)
    return { first: event.startsAt.slice(0, 10), last: lastDay.toISOString().slice(0, 10) }
  }

  const start = new Date(event.startsAt)
  const end = new Date(event.endsAt)
  // Une fin a minuit pile appartient a la veille : 22h-00h ne deborde pas sur le lendemain.
  const lastInstant = end.getTime() > start.getTime() ? new Date(end.getTime() - 1) : end

  return { first: dayKey(start), last: dayKey(lastInstant) }
}

export function occursOn(event: Spanning, key: string): boolean {
  const { first, last } = eventDays(event)
  return first <= key && key <= last
}

const monthTitle = new Intl.DateTimeFormat('fr-FR', { month: 'long', year: 'numeric' })
const longDay = new Intl.DateTimeFormat('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })
const shortDay = new Intl.DateTimeFormat('fr-FR', { day: 'numeric', month: 'short' })

const shortMoment = new Intl.DateTimeFormat('fr-FR', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })

/** Jour et heure en abrege, dans le fuseau du navigateur : « 4 oct., 17:33 ». */
export function formatMoment(iso: string): string {
  return shortMoment.format(new Date(iso))
}

export function formatMonth(date: Date): string {
  return monthTitle.format(date)
}

export function formatLongDay(date: Date): string {
  return longDay.format(date)
}

export function formatWeek(anchor: Date): string {
  const days = weekDays(anchor)
  return `${shortDay.format(days[0])} – ${shortDay.format(days[6])} ${days[6]!.getFullYear()}`
}

/** Periode d'un evenement, en clair. */
export function formatSpan(event: Spanning): string {
  const { first, last } = eventDays(event)

  if (event.isAllDay) {
    return first === last
      ? `${formatLongDay(fromDayKey(first))}, toute la journee`
      : `Du ${formatLongDay(fromDayKey(first))} au ${formatLongDay(fromDayKey(last))}`
  }

  const start = new Date(event.startsAt)
  const end = new Date(event.endsAt)

  return dayKey(start) === dayKey(end)
    ? `${formatLongDay(start)}, ${timeKey(start)} – ${timeKey(end)}`
    : `Du ${formatLongDay(start)} ${timeKey(start)} au ${formatLongDay(end)} ${timeKey(end)}`
}
